import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as hotSearchService from '../../services/hotSearchService';

// 异步 action：获取指定平台热搜数据
export const fetchHotSearchByPlatform = createAsyncThunk(
  'hotSearch/fetchByPlatform',
  async ({ platform, refresh = false }, { rejectWithValue }) => {
    try {
      const response = await hotSearchService.getHotSearchByPlatform(platform, refresh);
      return { platform, data: response };
    } catch (error) {
      return rejectWithValue({ platform, error: error.message });
    }
  }
);

// 异步 action：获取所有平台热搜数据
export const fetchAllHotSearch = createAsyncThunk(
  'hotSearch/fetchAll',
  async ({ refresh = false }, { rejectWithValue }) => {
    try {
      const response = await hotSearchService.getAllHotSearch(refresh);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步 action：获取支持的平台列表
export const fetchSupportedPlatforms = createAsyncThunk(
  'hotSearch/fetchPlatforms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await hotSearchService.getSupportedPlatforms();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步 action：刷新指定平台数据
export const refreshPlatformData = createAsyncThunk(
  'hotSearch/refreshPlatform',
  async ({ platform }, { rejectWithValue }) => {
    try {
      const response = await hotSearchService.refreshPlatformData(platform);
      return { platform, data: response };
    } catch (error) {
      return rejectWithValue({ platform, error: error.message });
    }
  }
);

// 异步 action：清除平台缓存
export const clearPlatformCache = createAsyncThunk(
  'hotSearch/clearPlatformCache',
  async ({ platform }, { rejectWithValue }) => {
    try {
      const response = await hotSearchService.clearPlatformCache(platform);
      return { platform, data: response };
    } catch (error) {
      return rejectWithValue({ platform, error: error.message });
    }
  }
);

const initialState = {
  // 平台数据
  platforms: {},
  // 支持的平台列表
  supportedPlatforms: [],
  // 加载状态
  loading: false,
  // 错误信息
  error: null,
  // 平台加载状态
  platformLoading: {},
  // 平台错误信息
  platformErrors: {},
  // 统计数据
  stats: null,
  // 最后更新时间
  lastUpdate: null
};

const hotSearchSlice = createSlice({
  name: 'hotSearch',
  initialState,
  reducers: {
    // 清除错误
    clearError: (state) => {
      state.error = null;
    },
    // 清除平台错误
    clearPlatformError: (state, action) => {
      const { platform } = action.payload;
      delete state.platformErrors[platform];
    },
    // 设置活跃平台
    setActivePlatform: (state, action) => {
      state.activePlatform = action.payload;
    },
    // 清除所有数据
    clearAllData: (state) => {
      state.platforms = {};
      state.platformErrors = {};
      state.lastUpdate = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取指定平台热搜数据
      .addCase(fetchHotSearchByPlatform.pending, (state, action) => {
        const { platform } = action.meta.arg;
        state.platformLoading[platform] = true;
        state.platformErrors[platform] = null;
      })
      .addCase(fetchHotSearchByPlatform.fulfilled, (state, action) => {
        const { platform, data } = action.payload;
        state.platforms[platform] = data;
        state.platformLoading[platform] = false;
        state.platformErrors[platform] = null;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchHotSearchByPlatform.rejected, (state, action) => {
        const { platform, error } = action.payload;
        state.platformLoading[platform] = false;
        state.platformErrors[platform] = error;
      })
      
      // 获取所有平台热搜数据
      .addCase(fetchAllHotSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllHotSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // 将数组数据转换为对象格式
        if (action.payload.data && Array.isArray(action.payload.data)) {
          action.payload.data.forEach(platformData => {
            if (platformData.platform) {
              state.platforms[platformData.platform] = platformData;
            }
          });
        }
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchAllHotSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取支持的平台列表
      .addCase(fetchSupportedPlatforms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportedPlatforms.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.supportedPlatforms = action.payload.data || [];
      })
      .addCase(fetchSupportedPlatforms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 刷新指定平台数据
      .addCase(refreshPlatformData.pending, (state, action) => {
        const { platform } = action.meta.arg;
        state.platformLoading[platform] = true;
        state.platformErrors[platform] = null;
      })
      .addCase(refreshPlatformData.fulfilled, (state, action) => {
        const { platform, data } = action.payload;
        state.platforms[platform] = data;
        state.platformLoading[platform] = false;
        state.platformErrors[platform] = null;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(refreshPlatformData.rejected, (state, action) => {
        const { platform, error } = action.payload;
        state.platformLoading[platform] = false;
        state.platformErrors[platform] = error;
      })
      
      // 清除平台缓存
      .addCase(clearPlatformCache.fulfilled, (state, action) => {
        const { platform } = action.payload;
        delete state.platforms[platform];
        delete state.platformErrors[platform];
      });
  }
});

export const { 
  clearError, 
  clearPlatformError, 
  setActivePlatform, 
  clearAllData 
} = hotSearchSlice.actions;

// Selectors
export const selectHotSearchPlatforms = (state) => state.hotSearch.platforms;
export const selectHotSearchPlatform = (state, platform) => state.hotSearch.platforms[platform];
export const selectSupportedPlatforms = (state) => state.hotSearch.supportedPlatforms;
export const selectHotSearchLoading = (state) => state.hotSearch.loading;
export const selectPlatformLoading = (state, platform) => state.hotSearch.platformLoading[platform];
export const selectHotSearchError = (state) => state.hotSearch.error;
export const selectPlatformError = (state, platform) => state.hotSearch.platformErrors[platform];
export const selectLastUpdate = (state) => state.hotSearch.lastUpdate;

export default hotSearchSlice.reducer; 